import {
    CreateParams,
    CreateResult,
    DataProvider,
    DeleteParams,
    DeleteResult,
    GetListParams,
    GetListResult,
    GetManyParams,
    GetManyResult,
    GetOneParams,
    GetOneResult,
    UpdateParams,
    UpdateResult,
} from 'react-admin';
import { read_cookie } from 'sfcookies';
import { apiUrl, UseUrlManagerCleanUrl } from '../components/utils/url-manager';
import { addSortToUrl, processFilters } from './provider/defaults';
import { LlmModelPrice } from '../types/entities';

/**
 * Provider for the LLM price list.
 *
 * Modelled on nokProcessingFailureDataProvider rather than on the 400-line template the older
 * resources copy, because those swallow a non-2xx and hand react-admin an empty result (HQ-06).
 * On this screen that would be actively harmful: an empty list reads as "no prices are set", which
 * is exactly what the dashboard reports when a model has no row, so a failed load and a genuinely
 * missing price would look identical while one is fixed by adding a row and the other is not.
 *
 * The one thing that must not silently fail here is a save. A rejected duplicate model name has to
 * reach the form as an error, or somebody types a second row for a model that already has one and
 * is told it worked.
 */

const headers = (contentType: string = 'application/json'): Headers => new Headers({
    'Content-Type': contentType,
    'token': read_cookie('auth'),
    'hq': 'hq',
});

/**
 * API Platform answers a validation failure with a description rather than a bare status, and that
 * sentence is the only thing that tells somebody a model name is already taken.
 */
const failureFrom = async (response: Response, fallback: string): Promise<Error> => {
    try {
        const body: any = await response.json();
        const detail: string = body?.['hydra:description'] ?? body?.detail ?? body?.description ?? '';

        return new Error(detail !== '' ? detail : fallback);
    } catch {
        return new Error(fallback);
    }
};

const fetchCollection = async (url: string): Promise<{ data: LlmModelPrice[], total: number }> => {
    const request: Request = new Request(UseUrlManagerCleanUrl({ url }), { method: 'GET', headers: headers() });
    const response: Response = await fetch(request);
    if (!response.ok) {
        throw await failureFrom(response, 'Could not load the model prices');
    }
    const body: any = await response.json();

    return {
        data: body?.['hydra:member'] ?? body?.member ?? [],
        total: body?.['hydra:totalItems'] ?? body?.totalItems ?? 0,
    };
};

const llmModelPriceDataProvider: DataProvider = {
    getList: async (resource: string, params: GetListParams): Promise<GetListResult> => {
        let url: string = apiUrl + resource;
        if (params.pagination) {
            url += `?page=${ params.pagination.page }&itemsPerPage=${ params.pagination.perPage }`;
        }
        url = processFilters(url, params);
        url = addSortToUrl(url, params);

        return fetchCollection(url);
    },

    getOne: async (resource: string, params: GetOneParams): Promise<GetOneResult> => {
        const url: string = UseUrlManagerCleanUrl({ url: `${ apiUrl }${ resource }/${ params.id }` });
        const response: Response = await fetch(new Request(url, { method: 'GET', headers: headers() }));
        if (!response.ok) {
            throw await failureFrom(response, 'Could not load the model price');
        }

        return { data: await response.json() };
    },

    getMany: async (resource: string, params: GetManyParams): Promise<GetManyResult> => {
        if (params.ids.length === 0) {
            return { data: [] };
        }
        const query: string = params.ids.map((id): string => `id[]=${ String(id) }`).join('&');
        const { data } = await fetchCollection(`${ apiUrl }${ resource }?${ query }`);

        return { data };
    },

    create: async (resource: string, params: CreateParams): Promise<CreateResult> => {
        const url: string = UseUrlManagerCleanUrl({ url: apiUrl + resource });
        // Stamped here rather than left to the form: createdAt is not something anybody should be
        // typing, and the column is not nullable.
        const body: any = { ...params.data, createdAt: new Date().toISOString() };
        const response: Response = await fetch(new Request(url, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: headers(),
        }));
        if (!response.ok) {
            throw await failureFrom(response, 'Could not save the model price');
        }

        return { data: await response.json() };
    },

    update: async (resource: string, params: UpdateParams): Promise<UpdateResult> => {
        const url: string = UseUrlManagerCleanUrl({ url: `${ apiUrl }${ resource }/${ params.id }` });
        const body: any = { ...params.data, updatedAt: new Date().toISOString() };
        const response: Response = await fetch(new Request(url, {
            method: 'PATCH',
            body: JSON.stringify(body),
            headers: headers('application/merge-patch+json'),
        }));
        if (!response.ok) {
            throw await failureFrom(response, 'Could not save the model price');
        }

        return { data: await response.json() };
    },

    delete: async (resource: string, params: DeleteParams): Promise<DeleteResult> => {
        const url: string = UseUrlManagerCleanUrl({ url: `${ apiUrl }${ resource }/${ params.id }` });
        const response: Response = await fetch(new Request(url, { method: 'DELETE', headers: headers() }));
        if (!response.ok) {
            throw await failureFrom(response, 'Could not delete the model price');
        }

        return { data: params.previousData as any };
    },

    // Bulk edits are off deliberately. Every row here is one model's price, and there is no change
    // that is correct to apply to several at once.
    getManyReference: (): Promise<never> => Promise.reject(new Error('Not supported')),
    updateMany: (): Promise<never> => Promise.reject(new Error('Not supported')),
    deleteMany: (): Promise<never> => Promise.reject(new Error('Not supported')),
};

export default llmModelPriceDataProvider;
